/**
 * Detalhe da despesa.
 *
 * O bloco de confirmação é o que faz o grupo concordar antes de fechar contas.
 * `Questionar` não altera valor nenhum: registra desacordo, que é a informação
 * que falta quando três pessoas lembram do jantar de jeitos diferentes.
 *
 * `Editar` e `Excluir` (teste físico, item 32) só aparecem para quem pode —
 * criador ou quem tem permissão sobre despesa alheia. A UI reflete; o
 * servidor decide, e recusa de qualquer jeito.
 */
import { formatMoney } from '@nv/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon } from '../../ui/icons';
import { Etiqueta } from '../../ui/Etiqueta';
import { Card, ConfirmSheet, ErrorState, SectionLabel, Skeleton } from '../../ui/primitives';
import { readExpense } from './api';
import { categoriaDe, rotuloDaCategoria } from './model';

/** Tom da etiqueta de categoria: o mesmo par de cor dos tiles do Financeiro. */
const TOM_DA_CATEGORIA = { 1: 'coral', 2: 'azul', 3: 'verde', 4: 'lilas' } as const;

const MODO: Record<string, string> = {
  equal: 'igual por pessoa',
  per_family: 'igual por família',
  percentage: 'por porcentagem',
  custom: 'valores escolhidos',
};
import { useFinanceStore } from './store';
import { FotoAvatar } from '../perfil/FotoAvatar';
import { usePessoas } from '../roteiro/people';

async function responder(
  tripId: string,
  expenseId: string,
  corpo: { state: 'confirmed' | 'questioned'; note?: string },
) {
  const res = await fetch(`/api/v1/trips/${tripId}/expenses/${expenseId}/confirmation`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', 'idempotency-key': crypto.randomUUID() },
    body: JSON.stringify(corpo),
  });
  if (!res.ok) {
    const erro = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(erro.message ?? 'Não foi possível registrar.');
  }
  return res.json();
}

export function DespesaScreen({
  tripId,
  nomes,
  meuMemberId,
  meuUserId,
  podeEditarDeOutros,
}: {
  tripId: string;
  nomes: Record<string, string>;
  meuMemberId: string;
  meuUserId: string;
  podeEditarDeOutros: boolean;
}) {
  const { fotoDe } = usePessoas();
  const { despesa: expenseId } = useParams<{ despesa: string }>();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const [nota, setNota] = useState('');
  const [questionando, setQuestionando] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const excluir = useFinanceStore((s) => s.excluir);

  const dados = useQuery({
    queryKey: ['financeiro', 'despesa', tripId, expenseId],
    queryFn: () => readExpense(tripId, expenseId ?? ''),
    enabled: Boolean(expenseId),
  });

  const confirmar = useMutation({
    mutationFn: (corpo: { state: 'confirmed' | 'questioned'; note?: string }) =>
      responder(tripId, expenseId ?? '', corpo),
    onSuccess: () => {
      setQuestionando(false);
      void cliente.invalidateQueries({ queryKey: ['financeiro'] });
    },
  });

  if (dados.isLoading) {
    return (
      <section className="fin">
        <Skeleton height={40} count={4} />
      </section>
    );
  }

  if (dados.isError) {
    return (
      <section className="fin">
        <ErrorState
          title="Despesa não encontrada"
          description="Ela pode ter sido excluída."
          onRetry={() => navegar('/financeiro')}
        />
      </section>
    );
  }

  const { expense: d, payments, splits, confirmations } = dados.data!;
  const nomeDe = (id: string | null) => (id ? (nomes[id] ?? 'Participante') : '—');
  const minhaParte = splits.find((s) => s.beneficiaryMemberId === meuMemberId);
  const minhaConfirmacao = confirmations.find((c) => c.memberId === meuMemberId);
  const souAutor = d.createdBy === meuUserId;
  const questionadas = confirmations.filter((c) => c.state === 'questioned');

  const cat = categoriaDe(d.category);
  const quando = new Date(d.spentAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', ' ·');
  const podeMexer = souAutor || podeEditarDeOutros;
  const iguais = splits.length > 1 && splits.every((s) => s.amountMinor === splits[0]!.amountMinor);
  const confirmadas = confirmations.filter((c) => c.state === 'confirmed');
  const faltam = confirmations.filter((c) => c.state === 'pending');

  return (
    <section className="fin fin-despesa">
      {/* Refino final: editar e excluir sobem para o cabeçalho, só para quem pode. */}
      <header className="fin-despesa__cab">
        {podeMexer && (
          <span className="fin-despesa__acoes">
            <button type="button" className="fin-despesa__acao" onClick={() => navegar(`/financeiro/${d.id}/editar`)} aria-label="Editar despesa" data-testid="despesa-editar">
              <Icon name="editar" size={18} />
            </button>
            <button type="button" className="fin-despesa__acao fin-despesa__acao--perigo" onClick={() => setConfirmandoExclusao(true)} aria-label="Excluir despesa" data-testid="despesa-excluir">
              <Icon name="excluir" size={18} />
            </button>
          </span>
        )}
      </header>

      <div className="fin-despesa__topo">
        <Etiqueta nome={`${rotuloDaCategoria(d.category)} · ${quando}`} icone={cat.icone} tom={TOM_DA_CATEGORIA[cat.tom]} />
        <h1 className="fin__detalhe-titulo">{d.description}</h1>
        <div className="fin__detalhe-valor">{formatMoney(d.totalMinor, d.currency)}</div>
      </div>

      <Card variant="surf" className="fin-despesa__conta">
        <div className="fin__detalhe-linha">
          <span>Pago por</span>
          <span className="fin__pessoas">
            {payments.map((p) => (
              <span key={p.payerMemberId} className="fin__pessoa">
                <FotoAvatar nome={nomeDe(p.payerMemberId)} url={fotoDe(p.payerMemberId).url} cor={fotoDe(p.payerMemberId).cor} tamanho={22} />
                {p.payerMemberId === meuMemberId ? 'Você' : nomeDe(p.payerMemberId)}
              </span>
            ))}
          </span>
        </div>
        <div className="fin__detalhe-linha">
          <span>Subtotal</span>
          <span>{formatMoney(d.subtotalMinor, d.currency)}</span>
        </div>
        {d.taxMinor > 0 && (
          <div className="fin__detalhe-linha">
            <span>Impostos</span>
            <span>{formatMoney(d.taxMinor, d.currency)}</span>
          </div>
        )}
        {d.tipMinor > 0 && (
          <div className="fin__detalhe-linha">
            <span>Gorjeta</span>
            <span>{formatMoney(d.tipMinor, d.currency)}</span>
          </div>
        )}
        {minhaParte && (
          <div className="fin__detalhe-linha fin__detalhe-linha--destaque">
            <span>Sua parte</span>
            <span>{formatMoney(minhaParte.amountMinor, d.currency)}</span>
          </div>
        )}
      </Card>

      {splits.length > 1 && (
        <>
          <SectionLabel>{`Divisão · ${splits.length} ${splits.length === 1 ? 'pessoa' : 'pessoas'}`}</SectionLabel>
          <div className="fin-despesa__divisao">
            {splits.map((s) => (
              <span className="fin-despesa__quem" key={s.beneficiaryMemberId ?? 'familia'}>
                <FotoAvatar nome={nomeDe(s.beneficiaryMemberId)} url={s.beneficiaryMemberId ? fotoDe(s.beneficiaryMemberId).url : null} cor={s.beneficiaryMemberId ? fotoDe(s.beneficiaryMemberId).cor : undefined} tamanho={28} />
                <span className="fin-despesa__quem-nome">{s.beneficiaryMemberId === meuMemberId ? 'Você' : nomeDe(s.beneficiaryMemberId).split(/\s+/)[0]}</span>
                {!iguais && <span className="fin-despesa__quem-valor">{formatMoney(s.amountMinor, d.currency)}</span>}
              </span>
            ))}
          </div>
          {iguais && (
            <p className="fin-despesa__cada">
              {formatMoney(splits[0]!.amountMinor, d.currency)} cada · {MODO[d.splitMode] ?? 'dividida'}
            </p>
          )}
        </>
      )}

      {minhaConfirmacao && minhaConfirmacao.state === 'pending' && !questionando && (
        <div className="fin__confirmacao nv-enter">
          <p className="fin__confirmacao-texto">Esta despesa inclui você. Confirma que está certa?</p>
          <div className="fin__ctas">
            <button type="button" className="nv-btn fin__confirmacao-questionar" onClick={() => setQuestionando(true)}>
              Questionar
            </button>
            <button
              type="button"
              className="nv-btn nv-btn--success"
              disabled={confirmar.isPending}
              onClick={() => confirmar.mutate({ state: 'confirmed' })}
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {minhaConfirmacao && minhaConfirmacao.state === 'confirmed' && (
        <div className="fin-despesa__confirmada nv-pop" role="status">
          <span className="fin-despesa__confirmada-icone" aria-hidden="true">
            <Icon name="concluir" size={18} />
          </span>
          <span className="fin-despesa__confirmada-texto">
            <b>Você confirmou</b>
            <span>
              {confirmadas.length} de {confirmations.length} confirmaram
              {faltam.length === 1 ? ` · falta ${nomeDe(faltam[0]!.memberId).split(/\s+/)[0]}` : faltam.length > 1 ? ` · faltam ${faltam.length}` : ''}
            </span>
          </span>
        </div>
      )}

      {questionando && (
        <div className="fin-despesa__questionar nv-enter">
          <p className="auth-note">
            Questionar não muda o valor. Registra o seu desacordo para o grupo
            resolver antes de fechar contas.
          </p>
          <textarea
            className="fin__nota-input"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="O que está errado?"
            aria-label="Motivo do questionamento"
          />
          <div className="fin__ctas">
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setQuestionando(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="nv-btn nv-btn--tinta"
              disabled={confirmar.isPending}
              onClick={() => confirmar.mutate({ state: 'questioned', note: nota.trim() })}
            >
              Registrar questionamento
            </button>
          </div>
        </div>
      )}

      {questionadas.length > 0 && (
        <Card variant="chip">
          <p className="auth-note">
            {questionadas.length === 1
              ? '1 pessoa questionou esta despesa.'
              : `${questionadas.length} pessoas questionaram esta despesa.`}{' '}
            Resolvam antes de fechar contas.
          </p>
        </Card>
      )}

      {confirmandoExclusao && (
        <ConfirmSheet
          titulo="Excluir esta despesa?"
          texto={`"${d.description}" sai do financeiro de todo mundo e dos saldos. O histórico continua explicando o que já foi acertado.`}
          confirmar="Excluir despesa"
          testId="confirmar-exclusao"
          onCancel={() => setConfirmandoExclusao(false)}
          onConfirm={() => {
            void excluir(d.id, d.version).then(() => {
              void cliente.invalidateQueries({ queryKey: ['financeiro'] });
              navegar('/financeiro', { replace: true });
            });
          }}
        />
      )}

      {confirmar.isError && (
        <p className="auth-error" role="alert">
          {(confirmar.error as Error).message}
        </p>
      )}
    </section>
  );
}
